import { z } from 'zod';
// Enums
export var TicketCategory;
(function (TicketCategory) {
    TicketCategory["ACADEMIC_INQUIRY"] = "ACADEMIC_INQUIRY";
    TicketCategory["BEHAVIORAL_CONCERN"] = "BEHAVIORAL_CONCERN";
    TicketCategory["ATTENDANCE_ISSUE"] = "ATTENDANCE_ISSUE";
    TicketCategory["FEE_INQUIRY"] = "FEE_INQUIRY";
    TicketCategory["TRANSPORT_ISSUE"] = "TRANSPORT_ISSUE";
    TicketCategory["HEALTH_MEDICAL"] = "HEALTH_MEDICAL";
    TicketCategory["FACILITY_COMPLAINT"] = "FACILITY_COMPLAINT";
    TicketCategory["COMMUNICATION"] = "COMMUNICATION";
    TicketCategory["ADMISSION_INQUIRY"] = "ADMISSION_INQUIRY";
    TicketCategory["EXTRACURRICULAR"] = "EXTRACURRICULAR";
    TicketCategory["TECHNICAL_SUPPORT"] = "TECHNICAL_SUPPORT";
    TicketCategory["OTHER"] = "OTHER";
})(TicketCategory || (TicketCategory = {}));
export var TicketPriority;
(function (TicketPriority) {
    TicketPriority["LOW"] = "LOW";
    TicketPriority["MEDIUM"] = "MEDIUM";
    TicketPriority["HIGH"] = "HIGH";
    TicketPriority["URGENT"] = "URGENT";
    TicketPriority["CRITICAL"] = "CRITICAL";
})(TicketPriority || (TicketPriority = {}));
export var TicketStatus;
(function (TicketStatus) {
    TicketStatus["OPEN"] = "OPEN";
    TicketStatus["IN_PROGRESS"] = "IN_PROGRESS";
    TicketStatus["PENDING_PARENT"] = "PENDING_PARENT";
    TicketStatus["RESOLVED"] = "RESOLVED";
    TicketStatus["CLOSED"] = "CLOSED";
    TicketStatus["ESCALATED"] = "ESCALATED";
})(TicketStatus || (TicketStatus = {}));
// Priority mapping based on category
export const CATEGORY_PRIORITY_MAP = {
    [TicketCategory.HEALTH_MEDICAL]: TicketPriority.URGENT,
    [TicketCategory.BEHAVIORAL_CONCERN]: TicketPriority.HIGH,
    [TicketCategory.ATTENDANCE_ISSUE]: TicketPriority.HIGH,
    [TicketCategory.FACILITY_COMPLAINT]: TicketPriority.HIGH,
    [TicketCategory.TRANSPORT_ISSUE]: TicketPriority.HIGH,
    [TicketCategory.FEE_INQUIRY]: TicketPriority.MEDIUM,
    [TicketCategory.ACADEMIC_INQUIRY]: TicketPriority.MEDIUM,
    [TicketCategory.ADMISSION_INQUIRY]: TicketPriority.MEDIUM,
    [TicketCategory.EXTRACURRICULAR]: TicketPriority.MEDIUM,
    [TicketCategory.COMMUNICATION]: TicketPriority.LOW,
    [TicketCategory.TECHNICAL_SUPPORT]: TicketPriority.LOW,
    [TicketCategory.OTHER]: TicketPriority.LOW,
};
// Category display names
export const CATEGORY_DISPLAY_NAMES = {
    [TicketCategory.ACADEMIC_INQUIRY]: 'Academic Inquiry',
    [TicketCategory.BEHAVIORAL_CONCERN]: 'Behavioral Concern',
    [TicketCategory.ATTENDANCE_ISSUE]: 'Attendance Issue',
    [TicketCategory.FEE_INQUIRY]: 'Fee Inquiry',
    [TicketCategory.TRANSPORT_ISSUE]: 'Transport Issue',
    [TicketCategory.HEALTH_MEDICAL]: 'Health & Medical',
    [TicketCategory.FACILITY_COMPLAINT]: 'Facility Complaint',
    [TicketCategory.COMMUNICATION]: 'General Communication',
    [TicketCategory.ADMISSION_INQUIRY]: 'Admission Inquiry',
    [TicketCategory.EXTRACURRICULAR]: 'Extracurricular Activities',
    [TicketCategory.TECHNICAL_SUPPORT]: 'Technical Support',
    [TicketCategory.OTHER]: 'Other',
};
// Priority display names and colors
export const PRIORITY_CONFIG = {
    [TicketPriority.LOW]: { name: 'Low', color: 'text-green-600', bgColor: 'bg-green-50' },
    [TicketPriority.MEDIUM]: { name: 'Medium', color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
    [TicketPriority.HIGH]: { name: 'High', color: 'text-orange-600', bgColor: 'bg-orange-50' },
    [TicketPriority.URGENT]: { name: 'Urgent', color: 'text-red-600', bgColor: 'bg-red-50' },
    [TicketPriority.CRITICAL]: { name: 'Critical', color: 'text-red-800', bgColor: 'bg-red-100' },
};
// Status display names and colors
export const STATUS_CONFIG = {
    [TicketStatus.OPEN]: { name: 'Open', color: 'text-blue-600', bgColor: 'bg-blue-50' },
    [TicketStatus.IN_PROGRESS]: { name: 'In Progress', color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
    [TicketStatus.PENDING_PARENT]: { name: 'Pending Parent', color: 'text-purple-600', bgColor: 'bg-purple-50' },
    [TicketStatus.RESOLVED]: { name: 'Resolved', color: 'text-green-600', bgColor: 'bg-green-50' },
    [TicketStatus.CLOSED]: { name: 'Closed', color: 'text-gray-600', bgColor: 'bg-gray-50' },
    [TicketStatus.ESCALATED]: { name: 'Escalated', color: 'text-red-600', bgColor: 'bg-red-50' },
};
// Zod schemas
export const TicketCategorySchema = z.nativeEnum(TicketCategory);
export const TicketPrioritySchema = z.nativeEnum(TicketPriority);
export const TicketStatusSchema = z.nativeEnum(TicketStatus);
export const CreateTicketSchema = z.object({
    title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
    description: z.string().min(1, 'Description is required').max(2000, 'Description must be less than 2000 characters'),
    category: TicketCategorySchema,
    studentId: z.string().uuid().optional(),
    attachments: z.array(z.string().url()).optional(),
    tags: z.array(z.string()).optional(),
});
export const UpdateTicketSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    description: z.string().min(1).max(2000).optional(),
    category: TicketCategorySchema.optional(),
    priority: TicketPrioritySchema.optional(),
    status: TicketStatusSchema.optional(),
    assignedToId: z.string().uuid().optional(),
    studentId: z.string().uuid().optional(),
    attachments: z.array(z.string().url()).optional(),
    tags: z.array(z.string()).optional(),
});
export const CreateTicketResponseSchema = z.object({
    content: z.string().min(1, 'Response content is required').max(2000, 'Response must be less than 2000 characters'),
    isInternal: z.boolean().default(false),
    attachments: z.array(z.string().url()).optional(),
});
export const TicketFiltersSchema = z.object({
    status: z.array(TicketStatusSchema).optional(),
    category: z.array(TicketCategorySchema).optional(),
    priority: z.array(TicketPrioritySchema).optional(),
    assignedToId: z.string().uuid().optional(),
    parentId: z.string().uuid().optional(),
    studentId: z.string().uuid().optional(),
    search: z.string().optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    page: z.number().min(1).default(1),
    limit: z.number().min(1).max(100).default(20),
    sortBy: z.enum(['raisedAt', 'updatedAt', 'priority', 'status']).default('raisedAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
// Utility functions
export function generateTicketNumber() {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `TKT-${timestamp}-${random}`;
}
export function getAutoPriority(category) {
    return CATEGORY_PRIORITY_MAP[category];
}
export function getCategoryDisplayName(category) {
    return CATEGORY_DISPLAY_NAMES[category];
}
export function getPriorityConfig(priority) {
    return PRIORITY_CONFIG[priority];
}
export function getStatusConfig(status) {
    return STATUS_CONFIG[status];
}
export function canTransitionStatus(from, to) {
    const validTransitions = {
        [TicketStatus.OPEN]: [TicketStatus.IN_PROGRESS, TicketStatus.CLOSED, TicketStatus.ESCALATED],
        [TicketStatus.IN_PROGRESS]: [TicketStatus.PENDING_PARENT, TicketStatus.RESOLVED, TicketStatus.ESCALATED, TicketStatus.OPEN],
        [TicketStatus.PENDING_PARENT]: [TicketStatus.IN_PROGRESS, TicketStatus.RESOLVED, TicketStatus.ESCALATED],
        [TicketStatus.RESOLVED]: [TicketStatus.CLOSED, TicketStatus.IN_PROGRESS],
        [TicketStatus.CLOSED]: [TicketStatus.IN_PROGRESS], // Can reopen if needed
        [TicketStatus.ESCALATED]: [TicketStatus.IN_PROGRESS, TicketStatus.RESOLVED],
    };
    return validTransitions[from]?.includes(to) || false;
}
//# sourceMappingURL=Ticket.js.map